// src/pages/admin/UserPanchayathAssign.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { Search, RefreshCcw, Save, User } from "react-feather";

import "../../../css/master-common.css";

import { fetchAdminUsersThunk, setSelectedUserId } from "../../../store/slices/adminPermissionSlice";

import {
  fetchAllPanchayathsThunk,
  fetchUserPanchayathsThunk,
  toggleAssign,
  setAllAssign,
  saveUserPanchayathsThunk,
  clearAssignOnly, // ✅ changed
} from "../../../store/slices/userPanchayathAssignSlice";

const Pill = ({ children }) => <Badge className="mm-pill">{children}</Badge>;

export default function UserPanchayathAssign() {
  const dispatch = useDispatch();

  const [userSearch, setUserSearch] = useState("");
  const [panSearch, setPanSearch] = useState("");

  const { users, selectedUserId, loadingUsers } = useSelector((s) => s.adminPerm);

  const { panchayaths, assignedMap, loadingPanchayaths, loadingAssigned, saving, error } =
    useSelector((s) => s.userPanAssign);

  useEffect(() => {
    dispatch(fetchAdminUsersThunk());
    dispatch(fetchAllPanchayathsThunk());

    // ✅ IMPORTANT: do NOT clear tenantTree here
    return () => dispatch(clearAssignOnly());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) dispatch(fetchUserPanchayathsThunk(selectedUserId));
  }, [dispatch, selectedUserId]);

  const selectedUser = useMemo(
    () => (users || []).find((u) => String(u.uid) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) => `${u.username} ${u.role}`.toLowerCase().includes(q));
  }, [users, userSearch]);

  const filteredPans = useMemo(() => {
    const q = panSearch.trim().toLowerCase();
    if (!q) return panchayaths || [];
    return (panchayaths || []).filter((p) =>
      `${p.pan_name} ${p.tenant_domain} ${p.db_name} ${p.district_name} ${p.taluk_name}`
        .toLowerCase()
        .includes(q)
    );
  }, [panchayaths, panSearch]);

  const assignedCount = useMemo(
    () => Object.values(assignedMap || {}).filter(Boolean).length,
    [assignedMap]
  );

  const canSave = !!selectedUserId && !saving;

  const onSave = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user first");
      return;
    }

    const res = await dispatch(saveUserPanchayathsThunk({ uid: selectedUserId, assignedMap }));

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success("Panchayath mapping updated ✅");
      dispatch(fetchUserPanchayathsThunk(selectedUserId));
    } else {
      toast.error(res?.payload || "Failed to save mapping");
    }
  };

  const onRefresh = () => {
    dispatch(fetchAdminUsersThunk());
    dispatch(fetchAllPanchayathsThunk());
    if (selectedUserId) dispatch(fetchUserPanchayathsThunk(selectedUserId));
  };

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">User ↔ Panchayath Mapping</h4>
          <div className="mm-subtitle">Select a user and assign allowed Panchayaths (tenant access)</div>
        </div>

        <div className="d-flex gap-2">
          <Button className="mm-cancel-btn d-flex align-items-center gap-2" onClick={onRefresh}>
            <RefreshCcw size={16} />
            Refresh
          </Button>

          <Button
            className="mm-primary-btn d-flex align-items-center gap-2"
            onClick={onSave}
            disabled={!canSave}
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Mapping"}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error ? <Alert variant="danger">{String(error)}</Alert> : null}

      {/* Top Controls */}
      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            {/* User dropdown */}
            <Col md={6}>
              <Form.Label className="fw-bold mb-1">Select User</Form.Label>

              <Dropdown className="w-100">
               <Dropdown.Toggle
  className="mm-dropdown-btn dropdown-toggle d-flex align-items-center justify-content-between"
  variant="light"
  disabled={loadingUsers}
>
  <span className="d-flex align-items-center gap-2">
    <User size={16} />
    <span className={selectedUser ? "mm-dropdown-label" : "mm-dropdown-placeholder"}>
      {selectedUser
        ? `${selectedUser.username} (${selectedUser.role})`
        : "Choose user"}
    </span>
  </span>

  <span className="mm-dropdown-arrow">▼</span>
</Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu">
                  <div className="px-2 pt-2 pb-2">
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search user..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <Dropdown.Divider />

                  <Dropdown.Item active={!selectedUserId} onClick={() => dispatch(setSelectedUserId(""))}>
                    -- Select --
                  </Dropdown.Item>

                  {(filteredUsers || []).map((u) => (
                    <Dropdown.Item
                      key={u.uid}
                      active={String(u.uid) === String(selectedUserId)}
                      onClick={() => dispatch(setSelectedUserId(u.uid))}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">{u.username}</span>
                        <Pill>{u.role}</Pill>
                      </div>
                    </Dropdown.Item>
                  ))}

                  {!filteredUsers?.length ? (
                    <div className="text-muted px-3 py-2 small">No users found</div>
                  ) : null}
                </Dropdown.Menu>
              </Dropdown>

              {loadingUsers ? (
                <div className="mt-2 text-muted small d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading users...
                </div>
              ) : null}
            </Col>

            {/* Selected user info */}
            <Col md={6} className="text-md-end">
              {selectedUser ? (
                <div className="d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                  <span className="text-muted">Editing:</span>
                  <span className="fw-bold">{selectedUser.username}</span>
                  <Pill>{selectedUser.role}</Pill>
                  <Pill>Assigned: {assignedCount}</Pill>
                </div>
              ) : (
                <div className="text-muted">Pick a user to begin</div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Panchayath search + bulk actions */}
      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-center">
            <Col md={6}>
              <Form.Label className="fw-bold mb-1">Search Panchayath</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search panchayath / domain / district / taluk..."
                  value={panSearch}
                  onChange={(e) => setPanSearch(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col md={6} className="d-flex gap-2 justify-content-md-end mt-3 mt-md-0">
              <Button
                className="mm-cancel-btn"
                onClick={() => dispatch(setAllAssign(true))}
                disabled={!selectedUserId}
              >
                Select All
              </Button>

              <Button
                className="mm-cancel-btn"
                onClick={() => dispatch(setAllAssign(false))}
                disabled={!selectedUserId}
              >
                Clear All
              </Button>

              {(loadingPanchayaths || loadingAssigned) && (
                <div className="text-muted d-flex align-items-center ms-2">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="mm-card">
        <Card.Body className="p-0">
          <div className="mm-table-wrap">
            <Table hover className="mm-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: 90, textAlign: "center" }}>Allow</th>
                  <th>Panchayath</th>
                  <th>District</th>
                  <th>Taluk</th>
                  <th>Tenant Domain</th>
                  <th style={{ width: 140 }}>Status</th>
                </tr>
              </thead>

              <tbody>
                {(filteredPans || []).map((p) => {
                  const checked = !!assignedMap?.[String(p.pan_id)];
                  return (
                    <tr key={p.pan_id}>
                      <td style={{ textAlign: "center" }}>
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          disabled={!selectedUserId}
                          onChange={(e) =>
                            dispatch(toggleAssign({ pan_id: p.pan_id, value: e.target.checked }))
                          }
                        />
                      </td>

                      <td className="fw-bold" title={p.pan_name}>
                        {p.pan_name}
                      </td>
                      <td>{p.district_name || "-"}</td>
                      <td>{p.taluk_name || "-"}</td>
                      <td>{p.tenant_domain || "-"}</td>

                      <td>
                        <Pill>{Number(p.status) === 1 ? "Active" : "Inactive"}</Pill>
                      </td>
                    </tr>
                  );
                })}

                {!filteredPans?.length ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      No panchayaths found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
